import { Component } from '@theme/component';
import { ReelPlayback, prefersReducedMotion } from '@theme/ola-reel';

/**
 * Ola · Garden Experience.
 *
 * - Pestañas de las ediciones (patrón tablist de ARIA): toque o clic muestra esa edición; con el
 *   teclado, flechas izquierda y derecha, Inicio y Fin pasan de pestaña y la muestran.
 * - Tira de fotos de cada edición: se desliza con el dedo, con el trackpad o con las flechas, que
 *   avanzan lo que se ve de una vez. El contador dice qué foto va primero y las flechas se apagan
 *   en los extremos.
 *
 * @typedef {object} Refs
 * @property {HTMLButtonElement[]} [tabs]
 * @property {HTMLElement[]} panels
 * @property {HTMLElement} [tablist]
 *
 * @extends {Component<Refs>}
 */
export class OlaGardenExperience extends Component {
  requiredRefs = ['panels'];

  /** @type {AbortController | undefined} */
  #controller;

  connectedCallback() {
    super.connectedCallback();
    this.#setup();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#setup();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller?.abort();
  }

  /**
   * Muestra la edición de la pestaña tocada.
   * @param {Event} event
   */
  select(event) {
    const tab = event.target instanceof Element ? event.target.closest('[role="tab"]') : null;
    if (!(tab instanceof HTMLButtonElement)) return;
    this.#activate(tab);
  }

  /**
   * Flechas, Inicio y Fin dentro de la fila de pestañas.
   * @param {KeyboardEvent} event
   */
  onKeydown(event) {
    const tabs = this.refs.tabs ?? [];
    const current = tabs.findIndex((tab) => tab === document.activeElement);
    if (current === -1) return;

    const isRtl = getComputedStyle(this).direction === 'rtl';
    const forward = isRtl ? 'ArrowLeft' : 'ArrowRight';
    const backward = isRtl ? 'ArrowRight' : 'ArrowLeft';

    let next = current;
    if (event.key === forward) next = (current + 1) % tabs.length;
    else if (event.key === backward) next = (current - 1 + tabs.length) % tabs.length;
    else if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = tabs.length - 1;
    else return;

    event.preventDefault();
    const tab = tabs[next];
    if (!tab) return;
    tab.focus({ preventScroll: true });
    this.#activate(tab);
  }

  /**
   * Avanza o retrocede la tira de fotos lo que se ve de una vez.
   * @param {Event} event
   */
  scrollStrip(event) {
    const button = event.target instanceof Element ? event.target.closest('[data-strip-direction]') : null;
    if (!(button instanceof HTMLElement)) return;
    const strip = this.#stripOf(button);
    if (!strip) return;

    const direction = Number(button.dataset.stripDirection) || 1;
    const isRtl = getComputedStyle(strip).direction === 'rtl';
    const step = this.#photoStep(strip) * Math.max(1, this.#visibleCount(strip));
    strip.scrollBy({
      left: step * direction * (isRtl ? -1 : 1),
      behavior: prefersReducedMotion() ? 'auto' : 'smooth',
    });
  }

  #setup() {
    this.#controller?.abort();
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    for (const strip of this.querySelectorAll('[data-strip]')) {
      if (!(strip instanceof HTMLElement)) continue;
      let frame = 0;
      const update = () => {
        if (frame) return;
        frame = window.requestAnimationFrame(() => {
          frame = 0;
          this.#updateStrip(strip);
        });
      };
      strip.addEventListener('scroll', update, { passive: true, signal });
      signal.addEventListener('abort', () => window.cancelAnimationFrame(frame));
      this.#updateStrip(strip);
    }

    window.addEventListener(
      'resize',
      () => {
        for (const strip of this.querySelectorAll('[data-strip]')) {
          if (strip instanceof HTMLElement) this.#updateStrip(strip);
        }
      },
      { passive: true, signal }
    );
  }

  /** @param {HTMLButtonElement} tab */
  #activate(tab) {
    const tabs = this.refs.tabs ?? [];
    const { panels } = this.refs;
    const index = tabs.indexOf(tab);
    if (index === -1) return;

    if (tab.getAttribute('aria-selected') !== 'true') {
      tabs.forEach((item, position) => {
        const selected = position === index;
        item.setAttribute('aria-selected', String(selected));
        item.tabIndex = selected ? 0 : -1;
      });

      panels.forEach((panel, position) => {
        panel.hidden = position !== index;
      });

      const strip = panels[index]?.querySelector('[data-strip]');
      if (strip instanceof HTMLElement) this.#updateStrip(strip);
    }

    this.#reveal(tab);
  }

  /**
   * Contador y flechas de una tira, según dónde quedó.
   * @param {HTMLElement} strip
   */
  #updateStrip(strip) {
    const panel = strip.closest('[ref="panels[]"]');
    if (!panel || strip.offsetParent === null) return;

    const step = this.#photoStep(strip);
    const max = strip.scrollWidth - strip.clientWidth;
    const position = Math.abs(strip.scrollLeft);
    const total = strip.children.length;
    const first = step > 0 ? Math.min(total, Math.round(position / step) + 1) : 1;

    const current = panel.querySelector('[data-strip-current]');
    if (current) current.textContent = String(first);

    for (const button of panel.querySelectorAll('[data-strip-direction]')) {
      if (!(button instanceof HTMLButtonElement)) continue;
      const back = button.dataset.stripDirection === '-1';
      button.disabled = back ? position <= 1 : position >= max - 1;
    }
  }

  /**
   * La tira de la misma edición que un botón.
   * @param {Element} element
   * @returns {HTMLElement | null}
   */
  #stripOf(element) {
    const strip = element.closest('[ref="panels[]"]')?.querySelector('[data-strip]');
    return strip instanceof HTMLElement ? strip : null;
  }

  /**
   * Ancho de una foto más el espacio entre fotos.
   * @param {HTMLElement} strip
   */
  #photoStep(strip) {
    const [first, second] = strip.children;
    if (!first) return 0;
    if (!second) return first.getBoundingClientRect().width;
    return Math.abs(second.getBoundingClientRect().left - first.getBoundingClientRect().left);
  }

  /**
   * Cuántas fotos enteras se ven a la vez.
   * @param {HTMLElement} strip
   */
  #visibleCount(strip) {
    const step = this.#photoStep(strip);
    const first = strip.firstElementChild;
    if (!step || !first) return 1;
    const gap = step - first.getBoundingClientRect().width;
    return Math.floor((strip.clientWidth + gap + 1) / step);
  }

  /**
   * Desliza la fila de pestañas (celular) para que la elegida quede a la vista, sin mover la página.
   * @param {HTMLElement} tab
   */
  #reveal(tab) {
    const { tablist } = this.refs;
    if (!tablist || tablist.scrollWidth <= tablist.clientWidth) return;

    const listBox = tablist.getBoundingClientRect();
    const tabBox = tab.getBoundingClientRect();
    const offset = tabBox.left - listBox.left - (listBox.width - tabBox.width) / 2;
    tablist.scrollBy({ left: offset, behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
  }
}

/**
 * Ola · Reel con botones: reproduce el video sin sonido y en bucle solo mientras se ve, con los
 * botones de sonido y de pausa de snippets/ola-reel-controles.liquid (ver `ReelPlayback`).
 *
 * @typedef {object} ReelRefs
 * @property {HTMLButtonElement} [soundButton]
 * @property {HTMLButtonElement} [pauseButton]
 *
 * @extends {Component<ReelRefs>}
 */
export class OlaReelPlayer extends Component {
  /** @type {ReelPlayback | undefined} */
  #playback;

  connectedCallback() {
    super.connectedCallback();
    this.#setup();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#setup();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#playback?.destroy();
    this.#playback = undefined;
  }

  /** Pausa o reproduce el reel. */
  togglePlay() {
    this.#playback?.togglePlay();
  }

  /** Activa o quita el sonido del reel. */
  toggleSound() {
    this.#playback?.toggleSound();
  }

  #setup() {
    this.#playback?.destroy();
    this.#playback = undefined;
    const video = this.querySelector('video');
    if (!video) return;
    this.#playback = new ReelPlayback(video, { pause: this.refs.pauseButton, sound: this.refs.soundButton });
  }
}

// Primero el reel, para que sus botones no queden como refs de la sección.
if (!customElements.get('ola-reel-player')) {
  customElements.define('ola-reel-player', OlaReelPlayer);
}

if (!customElements.get('ola-garden-experience')) {
  customElements.define('ola-garden-experience', OlaGardenExperience);
}
